import React, {useCallback, useEffect, useState, useMemo} from 'react'
import {Helmet} from 'react-helmet'
import {FormattedMessage} from 'react-intl'
import {useLocation} from 'react-router-dom'
import {useCategories, useProductSearch} from '@salesforce/commerce-sdk-react'
import {Box, Flex, useMultiStyleConfig} from '@chakra-ui/react'
import ManualsPageHeader from './partials/manuals-page-header'
import GridTemplate from '../../components/grid-template'
import GridCard from '../../components/grid-card'
import {CAT_MENU_DEFAULT_ROOT_CATEGORY} from '@salesforce/retail-react-app/app/constants'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useGetCategoriesData} from '../../custom-api/hooks/use-get-categories-data'
import {useParams} from 'react-router-dom/cjs/react-router-dom'
import useCustomGetCategory from '../../custom-hooks/use-custom-categories'
import routingObject from '../../utils/static-routes'
import useMulticountryUtils from '../../custom-hooks/use-multicountry-utils'
import {useManualsPageMeta} from '../../hooks/use-category-meta'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import AlternateMetaTag from '../../components/metatag/alternate'
import {getSites} from '../../utils/site-utils'
import LoadingSpinner from '../../components/loading-spinner'
import {useGetQuickLinks} from '../../custom-api/hooks/use-get-quicklinks'
import QuickLinks from '../faq/partials/quick-links'
import {useCustomPreferenceWithLocale} from '../../hooks/use-custom-preference'

const Manuals = () => {
    const styles = useMultiStyleConfig('Manuals')

    const [shouldShowResult, setShouldShowResult] = useState(false)
    const [productQuery, setProductQuery] = useState('')
    const location = useLocation()
    const {data: quickLinksData} = useGetQuickLinks()
    const {buildLocalizedUrl} = useMulticountryUtils()

    const {MANUALS_PATH} = routingObject

    const {site, locale} = useMultiSite()
    const {currency} = useCurrency()

    const params = useParams()
    const currentCategory = params.rest ? params.rest : CAT_MENU_DEFAULT_ROOT_CATEGORY

    const isNotRootCategory = currentCategory !== CAT_MENU_DEFAULT_ROOT_CATEGORY

    const categoryData = useGetCategoriesData()

    const {data: categoryDetails, isFetching: isCategoryDetailsFetching} = useCustomGetCategory(
        {
            parameters: {
                urlSegment: currentCategory,
            },
        },
        {
            enabled: isNotRootCategory,
        },
    )

    const categoryId = categoryDetails?.id ?? CAT_MENU_DEFAULT_ROOT_CATEGORY

    const {data: categoryResult, isFetching: isCategoriesListFetching} = useCategories(
        {
            parameters: {
                levels: 1,
                ids: [categoryId],
                locale: locale.id,
            },
        },
        {keepPreviousData: true},
    )

    const categories =
        categoryResult &&
        categoryResult.data &&
        categoryResult.data[0] &&
        categoryResult.data[0].categories

    const refine =
        (!categories || categories?.length === 0) && shouldShowResult && !productQuery
            ? ['cgid=' + categoryDetails?.id]
            : []

    const {data: productSearchResult, isFetching: isProductSearchFetching} = useProductSearch(
        {
            parameters: {
                q: productQuery.length > 0 ? productQuery : '',
                refine,
                expand: ['represented_products', 'page_meta_tags'],
                perPricebook: true,
                allVariationProperties: true,
                locale: locale.id,
                site: site.id,
                currency,
            },
        },
        {keepPreviousData: true},
    )

    const manualsCategoryHidden = useCustomPreferenceWithLocale('manualsCategoryHidden')

    const baseManualsUrl = buildLocalizedUrl(MANUALS_PATH)
    const {customParams} = getConfig()
    let canonicalUrl = `https://${customParams.currentHost}${baseManualsUrl}`
    if (isNotRootCategory && categoryDetails?.c_hreflangs) {
        const currentHreflangObj = categoryDetails.c_hreflangs.find(
            (item) => item.hreflang === locale.alias,
        )

        let url = currentHreflangObj ? currentHreflangObj.url : categoryDetails.c_hreflangs[0].url

        const split = url.split('/c/')
        if (split.length === 2) {
            url = `/${locale.alias}/manuals/c/${decodeURIComponent(split[1])}`
        }
        canonicalUrl = `https://${customParams.currentHost}${url}`
    }

    const getCategoryHref = useCallback(
        (category) => {
            const category_url = categoryData?.[category.id]?.url || ''
            return `${baseManualsUrl}${category_url}`
        },
        [categoryData, baseManualsUrl],
    )

    const handleInnerHeader = () => {
        if (productQuery.length > 0 && productSearchResult?.hits?.length > 0 && shouldShowResult) {
            return (
                <FormattedMessage
                    defaultMessage={`{number} results found for "{query}"`}
                    id="manuals-page.search-results"
                    values={{
                        number: productSearchResult?.hits?.length,
                        query: productQuery,
                    }}
                />
            )
        } else {
            return (
                <FormattedMessage
                    defaultMessage="Browse by category"
                    id="manuals-page.category-subheader"
                />
            )
        }
    }

    const getProductHref = (product) => {
        if (!product?.productId) return undefined
        return `/s/${product.productId}`
    }

    useEffect(() => {
        const categories =
            categoryResult &&
            categoryResult.data &&
            categoryResult.data[0] &&
            categoryResult.data[0].categories

        if (categories?.length === 0 || !categories) setShouldShowResult(true)
        else setShouldShowResult(false)
    }, [categoryResult])

    useEffect(() => {
        if (productQuery || productQuery.length > 0) setShouldShowResult(true)
    }, [productQuery])

    const isLoading = shouldShowResult
        ? isProductSearchFetching
        : isNotRootCategory
        ? isCategoryDetailsFetching || isCategoriesListFetching
        : isCategoriesListFetching

    const {metaTitle, metaDescription} = useManualsPageMeta(isNotRootCategory, categoryDetails)

    const currentPath = 'https://' + customParams.currentHost + location.pathname

    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: metaTitle,
        description: metaDescription,
        url: currentPath,
    }

    const sites = useMemo(() => getSites(), [])
    const calculateHreflangLinks = (isNotRootCategory, categoryDetails, sites) => {
        if (isNotRootCategory && categoryDetails?.c_hreflangs) {
            return categoryDetails.c_hreflangs.map((item) => {
                let url = item.url
                const split = url.split('/c/')
                if (split.length === 2) {
                    url = `/${item.hreflang}/manuals/c/${decodeURIComponent(split[1])}`
                }
                return {hreflang: item.hreflang, url}
            })
        }
        return sites
            .map((site) =>
                site.l10n.supportedLocales.map((locale) => ({
                    hreflang: locale.alias,
                    url: `/${locale.alias}/manuals`,
                })),
            )
            .flat()
    }
    const hreflangLinks = calculateHreflangLinks(isNotRootCategory, categoryDetails, sites)

    return (
        <Flex {...styles.manualsPageContainer} data-testid="manuals-page-container">
            <AlternateMetaTag links={hreflangLinks} />
            <Helmet>
                {metaTitle && <title>{metaTitle}</title>}
                {metaDescription && <meta name="description" content={metaDescription} />}
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
                <link rel="canonical" href={canonicalUrl} />
            </Helmet>
            <ManualsPageHeader
                noResults={!!(productQuery && shouldShowResult && !productSearchResult?.hits)}
                categoryId={categoryId}
                setProductQuery={setProductQuery}
                setShowResult={setShouldShowResult}
            />
            <GridTemplate header={handleInnerHeader()} variant="manuals">
                {isLoading && <LoadingSpinner />}
                {!shouldShowResult ||
                (productQuery && shouldShowResult && !productSearchResult?.hits) ? (
                    <>
                        {categories?.map((category) => {
                            // A category is hidden if its ID is in the custom preference and the value is 'false'.
                            const isHiddenByConfig =
                                manualsCategoryHidden &&
                                manualsCategoryHidden[category.id] === 'false'

                            // Categories with these terms in their ID should also be hidden.
                            const isGenerallyHidden =
                                category?.id.includes('commerceAdditional') ||
                                category?.id.includes('hidden')

                            // If the category is not hidden by any rule, render the card.
                            if (!isHiddenByConfig && !isGenerallyHidden) {
                                return (
                                    <GridCard
                                        key={category.id}
                                        title={category.name}
                                        href={getCategoryHref(category)}
                                        image={category?.c_imageLink}
                                        variant="manuals"
                                    />
                                )
                            }

                            // Otherwise, render nothing for this category.
                            return null
                        })}
                    </>
                ) : (
                    productSearchResult?.hits &&
                    productSearchResult?.hits?.map((product, i) => (
                        <GridCard
                            key={i}
                            title={product.productName}
                            href={getProductHref(product)}
                            image={
                                product?.c_image?.url ||
                                product?.image?.link ||
                                product?.image?.disBaseLink
                            }
                            variant="manuals"
                        />
                    ))
                )}
            </GridTemplate>
            {quickLinksData && (
                <Box {...styles.quicklinkContainer}>
                    <QuickLinks
                        quickLinksData={quickLinksData?.quick_link}
                        title={quickLinksData?.links_title}
                    />
                </Box>
            )}
        </Flex>
    )
}
Manuals.getTemplateName = () => 'manuals-page'
export default Manuals
